<script setup lang="ts">
import type { Callbacks, PaymentIntent } from '@chargebee/chargebee-js-types'

interface PaypalButtonStyleOptions {
  color: 'blue' | 'gold' | 'silver' | 'white' | 'black'
  shape: 'rect' | 'pill'
  label: 'pay' | 'checkout' | 'buynow' | 'paypal'
}

interface PaypalPaymentHandler {
  mountPaymentButton: (target: string, options: { style: PaypalButtonStyleOptions }) => Promise<void>
  handlePayment: (callbacks: Callbacks) => void
  setPaymentIntent: (intent: App.Data.Chargebee.PaymentIntentData | PaymentIntent) => void
  updatePaymentIntent: (intent: App.Data.Chargebee.PaymentIntentData | PaymentIntent) => void
}

withDefaults(defineProps<{
  amountToPay?: number
  disabled?: boolean
}>(), { amountToPay: 0, })

const emit = defineEmits<{
  paymentIntentAuthorized: [intent: PaymentIntent]
}>()

const { instance } = useChargebee()
const { activeWorkspaceId } = useAuth()
const { t } = useI18n()

const mountTargetId = `${useId()}-paypal-button`
const paypalHandler = ref<PaypalPaymentHandler>()
const paymentButtonMounted = ref(false)

const {
  mutate: createPaymentIntent,
  data: initialPaymentIntentData,
  isPending: isCreatingPaymentIntent,
} = useMutationChargebee.createPaymentIntent()

const {
  mutate: updatePaymentIntent,
  data: updatedPaymentIntentData,
  isPending: isUpdatingPaymentIntent,
} = useMutationChargebee.updatePaymentIntent()

const initialIntent = computed(() => initialPaymentIntentData.value?.data)
const updatedIntent = computed(() => updatedPaymentIntentData.value?.data)

const isPending = computed(() => isCreatingPaymentIntent.value || isUpdatingPaymentIntent.value)
const isValid = computed(() =>
  !isPending.value
  && !!initialIntent.value
  && paymentButtonMounted.value
  && !!paypalHandler.value,
)

whenever(instance, async (instance) => {
  paypalHandler.value = await instance.load('paypal')
}, { immediate: true, once: true })

whenever(() => __props.amountToPay, (amount) => {
  if (!activeWorkspaceId.value)
    return

  createPaymentIntent({
    workspaceId: activeWorkspaceId.value,
    payload: {
      amount,
      payment_method_type: 'paypal',
    },
  })
}, { once: true, immediate: true })

const { warning } = useToast()

const stopButtonMounter = watch([initialIntent, paypalHandler], ([intent, handler]) => {
  if (!intent || !handler)
    return

  handler.setPaymentIntent(intent)
  handler.mountPaymentButton(`#${mountTargetId}`, {
    style: {
      color: 'blue',
      shape: 'rect',
      label: 'pay',
    },
  })
    .then(() => {
      handler.handlePayment({
        success: (intent: PaymentIntent) => {
          emit('paymentIntentAuthorized', intent)
        },
        error: () => {
          warning(t('billing.paypal.paymentFailed'))
        },
      })
    })

  paymentButtonMounted.value = true

  nextTick(stopButtonMounter)
})

watch([() => __props.amountToPay, () => __props.disabled], ([amount, disabled], [oldAmount, _]) => {
  if (!(initialIntent.value && paymentButtonMounted.value) || disabled || amount === oldAmount || !activeWorkspaceId.value)
    return

  updatePaymentIntent({
    workspaceId: activeWorkspaceId.value,
    payload: {
      payment_intent_id: updatedIntent.value?.id || initialIntent.value.id,
      amount,
    },
  })
})

whenever(updatedIntent, (intent) => {
  if (!paypalHandler.value)
    return

  paypalHandler.value.updatePaymentIntent(intent)
})

defineExpose({
  isValid,
})
</script>

<template>
  <div
    class="inline-flex"
    :class="disabled ? 'pointer-events-none select-none opacity-20 cursor-not-allowed' : undefined"
  >
    <div
      :id="mountTargetId"
      class="inline-flex w-full items-center"
    />
  </div>
</template>
